import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../../data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { CustomButton10, CustomButton10Co, CustomButton10Ca, ModernDropdown } from '../../Templates';
import Spinner from '../../common/Spinner';
import { FaClock, FaMapMarkerAlt, FaCheckCircle, FaCalendarAlt } from 'react-icons/fa';
import { UnifiedDatePicker, UnifiedTimePicker } from '../../common/UnifiedDateTimePicker';

const PickupMeetingAgreement = ({ 
  delivery, 
  currentUser, 
  otherUser, 
  userRole, 
  packageData,
  onUpdate, 
  addSystemMessage, 
  createNotification 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showTimeSelector, setShowTimeSelector] = useState(false);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [waitingDuration, setWaitingDuration] = useState(3);

  // Determine user roles for pickup
  const isPackageSender = currentUser?.id === delivery?.sender_id;
  const isCarrier = currentUser?.id === delivery?.carrier_id;

  // Auto-populate with package travel_date and travel_time if available
  useEffect(() => {
    if (packageData?.travel_date && packageData?.travel_time && !selectedDate && !selectedTime) {
      // Set default date from package travel_date
      const travelDate = new Date(packageData.travel_date);
      setSelectedDate(travelDate);
      
      // Set default time from package travel_time
      const [hours, minutes] = packageData.travel_time.split(':');
      const timeDate = new Date();
      timeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      setSelectedTime(timeDate);
    }
  }, [packageData, selectedDate, selectedTime]);

  // Get combined datetime from date and time selections
  const getCombinedDateTime = () => {
    if (selectedDate && selectedTime) {
      const combined = new Date(selectedDate);
      combined.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
      return combined;
    }
    return null;
  };

  // Handle proposing pickup time
  const handleProposeTime = async () => {
    const combinedDateTime = getCombinedDateTime();
    
    if (!combinedDateTime) {
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.select_date_time')
      });
      return;
    }

    setLoading(true);
    
    try {
      const proposalData = {
        proposed_by: currentUser.id,
        proposed_time: combinedDateTime.toISOString(),
        proposed_at: new Date().toISOString(),
        waiting_duration: waitingDuration,
        status: 'pending'
      };

      // Update delivery with pickup meeting proposal
      const { error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          pickup_meeting_proposal: proposalData,
          status: 'pickup_meeting_agreement',
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id);

      if (updateError) throw updateError;

      // Fetch updated delivery data and call onUpdate
      const { data: updatedDelivery } = await supabase
        .from('package_deliveries')
        .select('*')
        .eq('id', delivery.id)
        .single();
      
      if (updatedDelivery && onUpdate) {
        onUpdate(updatedDelivery);
      }

      // Get other user ID for notification
      const otherUserId = isPackageSender ? delivery.carrier_id : delivery.sender_id;

      // Format time for display
      const timeString = combinedDateTime.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // Create notification
      await createNotification(
        otherUserId,
        'pickup_time_proposed',
        t('package_actions.pickup_time_proposed_notification', {
          time: timeString
        }),
        delivery.id
      );

      // Add system message
      await addSystemMessage(
        t('package_actions.pickup_time_proposed_system', {
          user: otherUser?.username || t('package_actions.user'),
          time: timeString
        })
      );

      addToast({
        type: 'success',
        title: t('package_actions.time_proposed'),
        description: t('package_actions.waiting_for_agreement')
      });

      setShowTimeModal(false);

    } catch (error) {
      console.error('Error proposing pickup time:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_proposing_time')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle responding to time proposal
  const handleTimeResponse = async (accept) => {
    setLoading(true);
    
    try {
      if (accept) {
        // Accept the proposed time
        const { error: updateError } = await supabase
          .from('package_deliveries')
          .update({
            pickup_meeting_time: delivery.pickup_meeting_proposal.proposed_time,
            pickup_meeting_agreed_at: new Date().toISOString(),
            pickup_timer_start_allowed_at: delivery.pickup_meeting_proposal.proposed_time,
            status: 'pickup_meeting_agreed',
            updated_at: new Date().toISOString()
          })
          .eq('id', delivery.id);

        if (updateError) throw updateError;

        // Fetch updated delivery data and call onUpdate
        const { data: updatedDelivery } = await supabase
          .from('package_deliveries')
          .select('*')
          .eq('id', delivery.id)
          .single();
        
        if (updatedDelivery && onUpdate) {
          onUpdate(updatedDelivery);
        }

        // Notify the proposer
        await createNotification(
          delivery.pickup_meeting_proposal.proposed_by,
          'pickup_time_accepted',
          t('package_actions.pickup_time_accepted_notification'),
          delivery.id
        );

        // Add system message
        const agreedTime = new Date(delivery.pickup_meeting_proposal.proposed_time);
        const timeString = agreedTime.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        
        await addSystemMessage(
          t('package_actions.pickup_time_agreed_system', {
            time: timeString
          })
        );

        addToast({
          type: 'success',
          title: t('package_actions.pickup_time_agreed'),
          description: t('package_actions.pickup_scheduled')
        });

      } else {
        // Decline the proposal
        const { error: updateError } = await supabase
          .from('package_deliveries')
          .update({
            pickup_meeting_proposal: null,
            status: 'confirmed',
            updated_at: new Date().toISOString()
          })
          .eq('id', delivery.id);

        if (updateError) throw updateError;

        // Fetch updated delivery data and call onUpdate
        const { data: updatedDelivery } = await supabase
          .from('package_deliveries')
          .select('*')
          .eq('id', delivery.id)
          .single();
        
        if (updatedDelivery && onUpdate) {
          onUpdate(updatedDelivery);
        }

        // Notify about decline
        await createNotification(
          delivery.pickup_meeting_proposal.proposed_by,
          'pickup_time_declined',
          t('package_actions.pickup_time_declined_notification'),
          delivery.id
        );

        // Add system message
        await addSystemMessage(
          t('package_actions.pickup_time_declined_system', { user: otherUser?.username || t('package_actions.user') })
        );

        addToast({
          type: 'info',
          title: t('package_actions.time_declined'),
          description: t('package_actions.propose_new_time')
        });
      }

    } catch (error) {
      console.error('Error responding to pickup time proposal:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_responding_time')
      });
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    return new Date();
  };

  // Render pickup location info
  const renderLocationInfo = () => (
    <div className="PAC-location-section">
      <div className="PAC-location-title">
        <FaMapMarkerAlt size={12} />
        {t('package_actions.pickup_location')}
      </div>
      <div className="PAC-location-address">
        {packageData?.address_from_street}, {packageData?.address_from_city}
        {packageData?.address_from_country && `, ${packageData.address_from_country}`}
      </div>
    </div>
  );

  // If there's an active proposal, show response options
  if (delivery?.pickup_meeting_proposal) {
    const proposal = delivery.pickup_meeting_proposal;
    const isMyProposal = proposal.proposed_by === currentUser?.id;
    const proposedTime = new Date(proposal.proposed_time);

    if (isMyProposal) {
      // Show waiting state for own proposal
      return (
        <div className="PAC-meeting-section">
          <div className="PAC-meeting-title">
            <FaClock size={14} />
            {t('package_actions.waiting_for_pickup_time_response')}
          </div>
          
          {renderLocationInfo()}
          
          <div className="PAC-meeting-time-display">
            {t('package_actions.proposed_time')}: {proposedTime.toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })}
          </div>
          
          <div style={{ fontSize: '11px', textAlign: 'center', color: 'var(--main-color)', opacity: 0.8 }}>
            {(() => {
              const role = isPackageSender ? t('package_actions.carrier') : t('package_actions.sender');
              const template = t('package_actions.waiting_for_other_response');
              return template.replace('{role}', role);
            })()}
          </div>
        </div>
      );
    } else {
      // Show response options for other user's proposal
      return (
        <div className="PAC-meeting-section">
          <div className="PAC-meeting-title">
            <FaClock size={14} />
            {t('package_actions.pickup_time_proposal')}
          </div>
          
          {renderLocationInfo()}
          
          <div className="PAC-meeting-time-display">
            {t('package_actions.proposed_time')}: {proposedTime.toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })}
          </div>
          
          <div className="PAC-action-buttons-v2">
            <CustomButton10Co
              onClick={() => handleTimeResponse(true)}
              disabled={loading}
            >
              {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.accept')}
            </CustomButton10Co>
            
            <CustomButton10Ca
              onClick={() => handleTimeResponse(false)}
              disabled={loading}
            >
              {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.decline')}
            </CustomButton10Ca>
          </div>
        </div>
      );
    }
  }

  // No active proposal - show time selection
  return (
    <div className="PAC-meeting-section">
      {renderLocationInfo()}
      
      <div style={{ fontSize: '11px', textAlign: 'center', marginBottom: '8px' }}>
        {t('package_actions.when_pickup_available')}
      </div>
      
      {!showTimeModal ? (
        <CustomButton10
          onClick={() => setShowTimeModal(true)}
          label={t('package_actions.propose_pickup_time')}
          disabled={loading}
        />
      ) : null}
      
      {/* Time Proposal Modal */}
      {showTimeModal && (
        <TimeProposalModal
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          waitingDuration={waitingDuration}
          onDateChange={setSelectedDate}
          onTimeChange={setSelectedTime}
          onWaitingDurationChange={setWaitingDuration}
          onCancel={() => {
            setShowTimeModal(false);
            // Reset to package defaults if cancelled
            if (packageData?.travel_date && packageData?.travel_time) {
              const travelDate = new Date(packageData.travel_date);
              setSelectedDate(travelDate);
              
              const [hours, minutes] = packageData.travel_time.split(':');
              const timeDate = new Date();
              timeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
              setSelectedTime(timeDate);
            }
          }}
          onConfirm={handleProposeTime}
          isProposing={loading}
          getMinDate={getMinDate}
        />
      )}
    </div>
  );
};

// Time Proposal Modal Component
const TimeProposalModal = ({
  selectedDate,
  selectedTime,
  waitingDuration,
  onDateChange,
  onTimeChange,
  onWaitingDurationChange,
  onCancel,
  onConfirm,
  isProposing,
  getMinDate
}) => {
  const { t } = useTranslation('common');

  const handleConfirm = () => {
    if (selectedDate && selectedTime) {
      onConfirm();
    }
  };

  return ReactDOM.createPortal(
    <div 
      className="PAC-modal-overlay-v2" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isProposing) {
          onCancel();
        }
      }}
    >
      <div className="PAC-modal2-v2">
        <div className="PAC-time-proposal-dialog">
          <div className="PAC-dialog-content">
            <h4>{t('package_actions.propose_pickup_time')}</h4>
            
            <div className="PAC-time-selector-grid">
              <div className="PAC-date-section">
                <label><FaCalendarAlt size={12} /> {t('package_actions.date')}</label>
                <UnifiedDatePicker
                  selected={selectedDate}
                  onChange={onDateChange}
                  theme="default"
                  placeholder={t('package_actions.select_date')}
                  minDate={getMinDate()}
                  className="PAC-modal-picker"
                />
              </div>
              
              <div className="PAC-time-section">
                <label><FaClock size={12} /> {t('package_actions.time')}</label>
                <UnifiedTimePicker
                  selected={selectedTime}
                  onChange={onTimeChange}
                  theme="default"
                  placeholder={t('package_actions.select_time')}
                  className="PAC-modal-picker"
                />
              </div>
              
              <div className="PAC-wait-section">
                <ModernDropdown
                  label={t('package_actions.wait_time')}
                  value={`${waitingDuration} ${t('package_actions.minutes')}`}
                  options={[
                    `3 ${t('package_actions.minutes')}`,
                    `5 ${t('package_actions.minutes')}`,
                    `10 ${t('package_actions.minutes')}`,
                    `15 ${t('package_actions.minutes')}`
                  ]}
                  onSelect={(option) => {
                    const minutes = parseInt(option.split(' ')[0]);
                    onWaitingDurationChange(minutes);
                  }}
                  icon={<FaClock size={12} />}
                  className="PAC-modal-dropdown"
                />
              </div>
            </div>
            
            {selectedDate && selectedTime && (
              <div className="PAC-time-preview">
                <div className="PAC-preview-title">
                  <FaCheckCircle size={12} /> {t('package_actions.proposed_time')}
                </div>
                <div className="PAC-preview-details">
                  {selectedDate.toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                  {' at '}
                  {selectedTime.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  })}
                </div>
                <div className="PAC-preview-wait">
                  {t('package_actions.wait_time')}: {waitingDuration} {t('package_actions.minutes')}
                </div>
              </div>
            )}
            
            <div className="PAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.cancel')}
                disabled={isProposing}
              />
              <CustomButton10Co
                onClick={handleConfirm}
                disabled={isProposing || !selectedDate || !selectedTime}
              >
                {isProposing ? (
                  <Spinner size="small" showText={false} />
                ) : (
                  t('package_actions.propose_time')
                )}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PickupMeetingAgreement;